import { useState, useEffect, useCallback } from 'react';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection.ts';
import { useDibsContracts } from 'dibs-crypto-utils/exports/useDibsContracts.ts';
import { useFragment, graphql } from 'react-relay';
import { type useMarketSettings_viewer$key } from './__generated__/useMarketSettings_viewer.graphql.ts';
import { type useMarketSettings_nftItem$key } from './__generated__/useMarketSettings_nftItem.graphql.ts';

export type MarketSettings = {
    royaltyRate: number;
    commissionRate: number;
    basisPoints: 100 | 10000;
};

export const useMarketSettings = ({
    viewer: viewerRef,
    nftItem: nftItemRef,
}: {
    viewer: useMarketSettings_viewer$key;
    nftItem: useMarketSettings_nftItem$key | null | undefined;
}): MarketSettings | null => {
    const [marketSettings, setMarketSettings] = useState<MarketSettings | null>(null);
    const { alchemyProvider } = useWeb3Connection();
    const [contracts] = useDibsContracts(alchemyProvider);

    const viewer = useFragment(
        graphql`
            fragment useMarketSettings_viewer on Viewer {
                isNftAuctionV2Enabled: featureFlag(feature: "nftAuctionV2")
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment useMarketSettings_nftItem on NftItem {
                tokenId
                contract {
                    address
                }
            }
        `,
        nftItemRef
    );

    const tokenContractAddress = nftItem?.contract?.address;
    const tokenId = nftItem?.tokenId;

    const isAuctionV2Enabled = viewer.isNftAuctionV2Enabled;

    const fetchMarketSettings = useCallback(async () => {
        if (contracts) {
            const useV2Contracts =
                !!isAuctionV2Enabled && !!contracts.marketSettingsV2 && !!contracts.tokenV2;

            let royalty: number = 0;

            if (isAuctionV2Enabled && contracts.royaltyEngine && tokenContractAddress && tokenId) {
                const [, amounts] = await contracts.royaltyEngine.getRoyaltyView(
                    tokenContractAddress,
                    tokenId,
                    10000
                );
                for (let i = 0; i < amounts.length; i++) {
                    royalty += amounts[i].toNumber();
                }
            } else if (useV2Contracts) {
                royalty = await contracts.tokenV2.globalCreatorRoyaltyBasisPoints();
            } else {
                royalty = await contracts.marketSettings.globalCreatorRoyaltyRate();
            }

            const [royaltyRate, commissionRate] = await Promise.all([
                royalty,
                useV2Contracts && contracts.marketSettingsV2
                    ? contracts.marketSettingsV2.globalMarketCommission()
                    : contracts.marketSettings.globalMarketCommission(),
            ]);

            setMarketSettings({
                royaltyRate: royaltyRate,
                commissionRate: commissionRate,
                basisPoints: isAuctionV2Enabled ? 10000 : 100,
            });
        }
    }, [contracts, isAuctionV2Enabled, tokenContractAddress, tokenId]);
    useEffect(() => {
        if (!marketSettings) {
            fetchMarketSettings();
        }
    }, [fetchMarketSettings, marketSettings]);

    return marketSettings;
};
